import {
  CpopScore,
  Dn4Score,
  MedicalOption,
  Priority,
  SurgerySeverity,
  SurgicalEvent
} from '@/modules/surgicalEvent';
import {
  ProtocolStatuses,
  QuestionnaireProtocolItem
} from '@/modules/protocol';
import { themeColors } from '@ianesthesie-kit/config';
import { PendingPatient } from '@/modules/pendingPatient';
import { XOR } from '@ianesthesie-kit/core';
import { ID } from '@ianesthesie-kit/services';

export type DashboardStatus<T> = T | 'DONE';
export type DashboardStatusColorMap<T extends string> = Partial<
  Record<DashboardStatus<T>, keyof typeof themeColors>
>;

export enum PreopStatus {
  UPCOMING_LONG_TERM = 'UPCOMING_LONG_TERM',
  UPCOMING_MID_TERM = 'UPCOMING_MID_TERM',
  ONE_DAY_BEFORE = 'ONE_DAY_BEFORE',
  UNKNOWN = 'SURGERY_DATE_UNKNOWN'
}

export enum PeropStatus {
  INPATIENT = 'INPATIENT',
  OUTPATIENT = 'OUTPATIENT',
  UNKNOWN = 'CARE_TYPE_UNKNOWN'
}

export enum PostopStatus {
  ONE_DAY_AFTER = 'ONE_DAY_AFTER',
  EARLY_POSTOP = 'EARLY_POSTOP',
  LATE_POSTOP = 'LATE_POSTOP'
}

export type SurgeryDateStatus = PreopStatus | PostopStatus;
export type ConsultationDateStatus = PreopStatus | 'TODAY';

export enum PreopView {
  DAY = 'DAY',
  UPCOMING = 'UPCOMING'
}

export interface BaseDashboardDto extends SurgicalEvent {
  surgeries: string[];
  surgeryDate?: string;
  questionnaireCompletedAt?: string;
  asaScore?: number;
  globalRisk?: number;
  careType?: string;
  automationCareType?: string;
  bmi?: number;
  statuses?: ProtocolStatuses;
}

/** Rows that carry the "seen" stamp of the surgery-planning protocol, see `SurgeryPlanningProtocol.seenAt`. */
export interface SeenStamp {
  seenAt?: string;
  seenBy?: ID;
}

export interface GenericDashboardDto extends BaseDashboardDto {
  medsCount?: number;
  hbLevel?: number;
  prepScore?: number;
}

export interface PreopDto extends GenericDashboardDto {
  anesthesiaConsultationDate?: string;
  anesthesiaConsultationTime?: string;
}

export interface PreopAllDto extends PreopDto, SeenStamp {
  surgeon?: string;
  anesthesiologist?: string;
  phoneConsultation?: 'YES' | 'NO' | 'IDK';
}

export type PreopAllPendingDto = {
  id: ID;
  isPending: true;
  anesthesiaConsultationDate: string;
  anesthesiaConsultationTime?: string;
  surgeryDate?: string;
  pendingAppointmentId: ID;
  patient: PendingPatient;
};

export type PossiblyPendingPreopAllDto = XOR<PreopAllDto, PreopAllPendingDto>;

export interface PreopLabTestingDto extends PreopAllDto {
  /** Instant */
  labOrderSentAt?: string;
  labOrderSent: boolean;
}

export type PreopTelehealthDto = PreopDto;

export interface PreopSmokerDto extends PreopDto {
  wantsHelpToStop?: boolean;
  firstCigDelay?: string;
  cigsPerDay?: number;
  recommendedNicotineDosage?: string;
  prescriptionSentAt?: string;
}

export interface PreopCardiacStressDto extends PreopDto {
  leeScoreResult?: number;
  cardiacRiskFactorResult?: number;
  metsUnderFourOrUndefined?: boolean;
  surgerySeverity?: SurgerySeverity;
  hasReferrals?: boolean;
}

export interface PreopAnemicDto extends PreopDto {
  mcvLevel?: number;
  plateletsLevel?: number;
  dfgLevel?: number;
  chronicKidneyDiseaseStatus?: string;
  copilotAvailable?: boolean;
}

export interface PreopSleepApneaDto extends PreopDto {
  stopBangScore?: number;
}

export interface PreopChronicPainDto extends PreopDto {
  comments?: string;
  cpopScoreDetails?: CpopScore;
}

export interface PreopSurgeryPlanningDto extends PreopDto, SeenStamp {
  surgeon?: string;
  comments?: string;
  surgeryRisk?: SurgerySeverity;
  plannedAnesthesiaTypes: string[];
  allergies: string[];
  invoicePrice?: number;
}

export type PredefinedSeverityFilter = Exclude<
  SurgerySeverity,
  'NOT_APPLICABLE'
>;

export type SurgeryPlanningPredefinedCommentsFilter = 'NOT_BLANK';

export type PreopSurgeryPlanningFilters = {
  date?: { dateFrom: string; dateTo: string | null };
  severity?: PredefinedSeverityFilter[];
  comments?: SurgeryPlanningPredefinedCommentsFilter;
  surgeon?: string[];
  anesthesiologist?: string[];
  asaScore?: number[];
  consentStatus?: SentSignedStatus[];
  globalRisk?: number[];
  priority?: Priority[];
};

type SentSignedStatus = 'NOT_SENT' | 'SENT' | 'SIGNED';

export type PreopDayViewFilters = {
  surgeon?: string[];
  anesthesiologist?: string[];
  consentStatus?: SentSignedStatus[];
  invoiceStatus?: SentSignedStatus[];
  seen?: 'SEEN' | 'NOT_SEEN';
  globalRisk?: number[];
  priority?: Priority[];
};

export interface PeropAllDto extends GenericDashboardDto {
  surgeon?: string;
  plannedAnesthesiaTypes: string[];
  allergies: string[];
  invoicePrice?: number;
  staffNotesContent?: string;
}

export interface PostopAllDto extends GenericDashboardDto {
  surgeon?: string;
  plannedAnesthesiaTypes: string[];
  allergies: string[];
  invoicePrice?: number;
  staffNotesContent?: string;
}

export type PeropFilters = {
  severity?: PredefinedSeverityFilter[];
  surgeon?: string[];
  anesthesiologist?: string[];
  asaScore?: number[];
  consentStatus?: SentSignedStatus[];
  priority?: Priority[];
};

export type PostopFilters = {
  date?: { dateFrom: string; dateTo: string | null };
  surgeon?: string[];
  anesthesiologist?: string[];
  asaScore?: number[];
  consentStatus?: SentSignedStatus[];
  priority?: Priority[];
};

export interface EarlyPostopDto extends BaseDashboardDto {
  painLevel: number;
  temperature: number;
  ponvLevel: string;
  bleedingLevel: string;
  hospitalized: string;
  surgery: string;
  patientPostopComments: string;
  assignedQuestionnaireItems: QuestionnaireProtocolItem[];
  postopComplications?: MedicalOption[];
}

export interface PostopChronicPainDto extends GenericDashboardDto {
  dn4VasPain?: number;
  comments?: string;
  cpopScoreDetails?: CpopScore;
  dn4ScoreDetails?: Dn4Score;
}

export const DASHBOARD_PAGE_SIZE = 5;
export const DASHBOARD_DAY_VIEW_PAGE_SIZE = 500;
export const DASHBOARD_DAY_VIEW_CLIENT_SIDE_PAGINATION_PAGE_SIZE = 100;
